/**
 * Admin "Portfolio leads" panel — visitors who submitted their cross-portfolio
 * maturity from the /services PortfolioRadar "Send my portfolio" CTA.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "../../lib/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso || "—"; }
};

const SLUG_TO_CODE = {
  itsm360: "ITSM360", ai360: "AI360", data360: "DATA360", pmo360: "PMO360",
  change360: "CHANGE360", digital360: "DIGITAL360", operating360: "OPERATING360",
  supplier360: "SUPPLIER360", benefits360: "BENEFITS360", govern360: "GOVERN360",
  capability360: "CAPABILITY360", stakeholder360: "STAKEHOLDER360",
};

export default function PortfolioLeadsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/portfolio-leads");
      setItems(data.items || []);
    } catch (err) {
      toast.error("Failed to load portfolio leads");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const exportCsv = () => {
    const a = document.createElement("a");
    a.href = `${API_BASE}/admin/portfolio-leads.csv`;
    a.setAttribute("download", "ekselens-portfolio-leads.csv");
    a.setAttribute("rel", "noopener");
    a.click();
  };

  return (
    <div data-testid="admin-portfolio-leads-panel">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Portfolio leads</h2>
          <p className="text-zinc-500 text-[13px] mt-1">Cross-portfolio maturity hand-raises from the <code className="text-[12px]">/services</code> radar. {items.length} total.</p>
        </div>
        <button type="button" onClick={exportCsv} className="ek-btn ek-btn-outline text-[12px]" data-testid="portfolio-leads-csv"><Download size={13}/> Export CSV</button>
      </div>
      {loading ? (
        <p className="text-zinc-500 text-[13px]">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-8 text-center">
          <p className="text-zinc-600 text-[14px]">No portfolio hand-raises yet. Once a visitor completes ≥1 lite self-assessment and submits their portfolio, they'll appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-zinc-200">
          <table className="min-w-full text-[13px]">
            <thead className="bg-zinc-50 text-left text-zinc-500 text-[11px] uppercase tracking-[0.12em]">
              <tr>
                <th className="p-3">Date</th>
                <th className="p-3">Name</th>
                <th className="p-3">Email</th>
                <th className="p-3">Organisation</th>
                <th className="p-3">Sector</th>
                <th className="p-3">Completed</th>
                <th className="p-3">Avg score</th>
                <th className="p-3">Sub-brands</th>
                <th className="p-3">PDF email</th>
                <th className="p-3">Note</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-zinc-100 hover:bg-zinc-50/50 align-top" data-testid={`plead-${it.id}`}>
                  <td className="p-3 text-zinc-600 whitespace-nowrap">{fmtDate(it.created_at)}</td>
                  <td className="p-3 font-medium">{it.full_name}</td>
                  <td className="p-3"><a href={`mailto:${it.email}`} className="text-[#7737F6] hover:underline">{it.email}</a></td>
                  <td className="p-3 text-zinc-700">{it.organisation || "—"}</td>
                  <td className="p-3 text-zinc-600">{it.sector || "—"}</td>
                  <td className="p-3 tabular-nums text-zinc-800">{it.completed_count} / 12</td>
                  <td className="p-3 tabular-nums">
                    <span className="inline-block bg-[#7737F6] text-white px-2 py-0.5 text-[11px] font-semibold">
                      {it.portfolio_overall_avg} / 4
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1 max-w-[280px]">
                      {(it.completed_slugs || []).map((s) => {
                        const score = it.snapshots?.[s]?.overall;
                        return (
                          <span key={s} className="inline-block bg-[#F4EEFD] text-[#7737F6] text-[10.5px] px-1.5 py-0.5 border border-[#7737F6]/30">
                            {SLUG_TO_CODE[s] || s}{score ? ` ${score}` : ""}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="p-3">
                    {(() => {
                      const s = it.email_status || "pending";
                      const colour = s === "sent" ? "bg-emerald-100 text-emerald-700" : s === "failed" ? "bg-red-100 text-red-700" : s === "skipped" ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-600";
                      return <span className={`inline-block ${colour} text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5`}>{s}</span>;
                    })()}
                  </td>
                  <td className="p-3 text-[12px] text-zinc-600 max-w-[260px]">{it.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
